import React, { useEffect, useMemo, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { useDispatch, useSelector } from 'react-redux'
import { Modal, ModalBody } from 'reactstrap'
import { addToCollection, createCollection, removeFromCollectionAPI } from 'services/api/collection'
import { fetchCollectionsThunk, fetchCreatorsCollectionItemsThunk, fetchPostCollectionItemsThunk } from 'slices/collections/thunk'
import { addBookmarkCollection as addBookmarkCollectionCAPost, cleanContentFilteredLists, removeBookmarkCollection as removeBookmarkCollectionCAPost } from 'slices/competitionAnalysis/reducer'
import { AppDispatch, RootState } from 'store'
import { SocialPost } from 'types/campaign/collaborations'
import { SocialAccount } from 'types/influencer'
import { textToTitleCase } from 'utils/helpers'
import { addBookmarkCollectionCreator, addBookmarkCollection as addBookmarkCollectionSAPost, cleanContentStateAll, cleanCreatorsState, cleanCreatorsStateAll, removeBookmarkCollectionCreator, removeBookmarkCollection as removeBookmarkCollectionSAPost } from 'slices/socialAccounts/reducer'
import { DEFAULT_CREATOR_COLLECTION_ID, DEFAULT_POST_COLLECTION_ID } from 'constants/global'
interface ManageCollectionModalProps {
    isOpen: boolean,
    post: SocialPost | null,
    creator: SocialAccount | null,
    itemType: 'post' | 'account',
    addMode: boolean,
    currentCollectionId?: string,
    onClose: () => void
}
const ManageCollectionModal = ({ isOpen, post, creator, itemType, onClose, addMode, currentCollectionId }: ManageCollectionModalProps) => {

    const collectionsData = useSelector((state: RootState) => state.collections.list?.items);
    const collections = (collectionsData ?? []).filter((c) => c.type === itemType);
    const [newCollection, setNewCollection] = useState(false);
    const [collectionName, setCollectionName] = useState('');
    const [creating, setCreating] = useState(false);
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const [processing, setProcessing] = useState<string | null>(null);
    const [bookmarkCollections, setBookmarkCollections] = useState<string[]>([]);

    const thumbnail = itemType === 'post' ? post?.thumbnail_url : creator?.profile_picture_url;

    const defaultCollectionId = useMemo(() => {
        return itemType === 'post' ? DEFAULT_POST_COLLECTION_ID : DEFAULT_CREATOR_COLLECTION_ID;
    }, [itemType])

    const [loading, setLoading] = useState(false);
    const dispatch: AppDispatch = useDispatch();

    const sortedCollections = useMemo(() => {
        const currentCollection = collections.find((c) => c.uuid === currentCollectionId);
        if (!currentCollectionId || !currentCollection || [DEFAULT_POST_COLLECTION_ID, DEFAULT_CREATOR_COLLECTION_ID].includes(currentCollectionId)) return collections;
        return [currentCollection, ...collections.filter((c) => c.uuid !== currentCollectionId)];
    }, [currentCollectionId, collections])

    const resetNewCollectionState = () => {
        setCollectionName('');
        setNewCollection(false);
    }

    useEffect(() => {
        let bookmark_collections = (itemType === 'post' ? post?.bookmark_collections : creator?.bookmark_collections) as string[] ?? [];
        if (currentCollectionId && ![DEFAULT_POST_COLLECTION_ID, DEFAULT_CREATOR_COLLECTION_ID].includes(currentCollectionId) && !bookmark_collections.includes(currentCollectionId)) {
            bookmark_collections.push(currentCollectionId);
        }
        setBookmarkCollections(bookmark_collections);
    }, [post?.bookmark_collections, creator?.bookmark_collections, itemType, currentCollectionId])

    useEffect(() => {
        if (isOpen && addMode) {
            resetNewCollectionState()
            setLoading(true);
            bookmark().finally(() => dispatch(fetchCollectionsThunk({})).finally(() => setLoading(false)));
        }
    }, [isOpen, addMode])

    const create = async () => {
        if (!collectionName || !selectedAgencyId) return;
        try {
            setCreating(true);
            const { collection } = await createCollection(selectedAgencyId, { name: collectionName, type: itemType });
            if (collection) {
                await bookmark(collection.uuid);
                await dispatch(fetchCollectionsThunk({ force: true }));
                resetNewCollectionState();
            }
        } catch (error) {
            console.log(error);
        } finally {
            setCreating(false);
        }
    }


    const bookmark = async (collectionId?: string) => {
        const isBookmarked = (itemType === 'post' ? post?.is_bookmarked : creator?.is_bookmarked) ?? false;
        if (!collectionId && isBookmarked) return;
        const uuid = itemType === 'post' ? post?.uuid : creator?.uuid;
        const socialAccountId = itemType === 'post' ? post?.social_account?.uuid : creator?.uuid;
        if (!selectedAgencyId || !uuid || !socialAccountId) return;
        try {
            setProcessing(collectionId ?? defaultCollectionId);
            await addToCollection(selectedAgencyId, uuid, itemType, collectionId);
            if (itemType === 'post') {
                dispatch(addBookmarkCollectionCAPost({ postId: uuid, socialAccountId, collectionId: collectionId ?? defaultCollectionId }))
                dispatch(addBookmarkCollectionSAPost({ postId: uuid, socialAccountId, collectionId: collectionId ?? defaultCollectionId }))
            } else {
                dispatch(addBookmarkCollectionCreator({ socialAccountId, collectionId: collectionId ?? defaultCollectionId }))
            }
            let updatedBookmarksCollections = [...bookmarkCollections];
            if (collectionId) {
                updatedBookmarksCollections.push(collectionId);
            }
            setBookmarkCollections(updatedBookmarksCollections);
            await dispatch(fetchCollectionsThunk({ force: true }));
            if(currentCollectionId && collectionId && collectionId === currentCollectionId) {
                refetchCurrentCollection();
                if(itemType === 'post'){
                    dispatch(cleanContentFilteredLists());
                    dispatch(cleanContentStateAll());
                }else if(itemType === 'account'){
                    dispatch(cleanCreatorsStateAll());
                }
            }
        } catch (error) {
            console.log(error);
        } finally {
            setProcessing(null);
        }
    }

    const removeFromCollection = async (collectionId?: string) => {
        const uuid = itemType === 'post' ? post?.uuid : creator?.uuid;
        const socialAccountId = itemType === 'post' ? post?.social_account?.uuid : creator?.uuid;
        if (!selectedAgencyId || !uuid) return;
        try {
            setProcessing(collectionId ?? defaultCollectionId);
            await removeFromCollectionAPI(selectedAgencyId, uuid, itemType, collectionId);
            if (itemType === 'post') {
                dispatch(removeBookmarkCollectionCAPost({ postId: uuid, socialAccountId, collectionId: collectionId ?? defaultCollectionId }))
                dispatch(removeBookmarkCollectionSAPost({ postId: uuid, socialAccountId, collectionId: collectionId ?? defaultCollectionId }))
            } else {
                dispatch(removeBookmarkCollectionCreator({ socialAccountId, collectionId: collectionId ?? defaultCollectionId }))
            }
            let updatedBookmarksCollections = [...bookmarkCollections].filter((c) => c !== collectionId);
            setBookmarkCollections(updatedBookmarksCollections);
            await dispatch(fetchCollectionsThunk({ force: true }));
            if(currentCollectionId && collectionId && collectionId === currentCollectionId) {
                refetchCurrentCollection();
                if(itemType === 'post'){
                    dispatch(cleanContentFilteredLists());
                    dispatch(cleanContentStateAll());
                }else if(itemType === 'account'){
                    dispatch(cleanCreatorsStateAll());
                }
            }
        } catch (error) {
            console.log(error);
        } finally {
            setProcessing(null);
            if (!collectionId) {
                onClose();
            }
        }
    }

    const refetchCurrentCollection = async () => {
        if (currentCollectionId) {
            if(itemType === 'post'){
                dispatch(fetchPostCollectionItemsThunk({ collectionId: currentCollectionId, filters: { page: 1 }, force: true }))
            }else if(itemType === 'account'){
                dispatch(fetchCreatorsCollectionItemsThunk({ collectionId: currentCollectionId, filters: { page: 1 }, force: true }))
            }
        }
    }


    return (
        <Modal isOpen={isOpen} toggle={onClose} className='add-to-collection-modal'>
            {
                newCollection ? (
                    <ModalBody className='p-0 bg-white'>
                        <div className="new-collection-header">
                            <button onClick={() => setNewCollection(false)} className="unset-all text-primary new-collection-btn">Cancel</button>
                            <h6 className='collection-title m-0'>New Collection</h6>
                            <button onClick={create} className="unset-all text-primary new-collection-btn" disabled={!collectionName || creating}>{creating ? 'Saving...' : 'Save'}</button>
                        </div>
                        <div className="new-collection-body">
                            <div className="d-flex align-items center justify-content-center">
                                <div className='new-collection-thumbnail bgc-content bg-image' style={{ backgroundImage: `url(${thumbnail})` }}></div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="collection-name">Collection Name</label>
                                <input type="text" value={collectionName} onChange={(e) => setCollectionName(e.target.value)} className="form-control" id="collection-name" placeholder="Enter collection name" />
                            </div>
                        </div>
                    </ModalBody>
                ) : (
                    <ModalBody className='p-0'>
                        {
                            addMode ? (
                                <div className="add-to-collection-header">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="add-to-collection-thumbnail bgc-content bg-image" style={{ backgroundImage: `url(${thumbnail})` }}></div>
                                        <div>
                                            <h6 className='add-to-collection-title'>{processing === defaultCollectionId ? <Skeleton height={20} width={100} /> : 'Saved'}</h6>
                                        </div>
                                    </div>
                                    {
                                        processing === defaultCollectionId ? (
                                            <span className="spinner-border flex-shrink-0 text-primary" style={{ height: '1rem', width: '1rem' }} role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </span>
                                        ) : (
                                            <button className='unset-all' disabled={!!processing} onClick={() => removeFromCollection()}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M21 11.0975V16.0909C21 19.1875 21 20.7358 20.2659 21.4123C19.9158 21.735 19.4739 21.9377 19.0031 21.9915C18.016 22.1045 16.8633 21.0849 14.5578 19.0458C13.5388 18.1445 13.0292 17.6938 12.4397 17.5751C12.1494 17.5166 11.8506 17.5166 11.5603 17.5751C10.9708 17.6938 10.4612 18.1445 9.44216 19.0458C7.13673 21.0849 5.98402 22.1045 4.99692 21.9915C4.52615 21.9377 4.08421 21.735 3.73411 21.4123C3 20.7358 3 19.1875 3 16.0909V11.0975C3 6.80891 3 4.6646 4.31802 3.3323C5.63604 2 7.75736 2 12 2C16.2426 2 18.364 2 19.682 3.3323C21 4.6646 21 6.80891 21 11.0975ZM8.25 6C8.25 5.58579 8.58579 5.25 9 5.25H15C15.4142 5.25 15.75 5.58579 15.75 6C15.75 6.41421 15.4142 6.75 15 6.75H9C8.58579 6.75 8.25 6.41421 8.25 6Z" fill="#0BAC68" />
                                                </svg>
                                            </button>
                                        )
                                    }
                                </div>
                            ) : (
                                <div className="add-to-collection-header">
                                    <div className="d-flex align-items-center justify-content-start gap-3 w-100">
                                        <div className="add-to-collection-thumbnail bgc-content bg-image" style={{ backgroundImage: `url(${thumbnail})`, height: '5rem', width: '5rem' }}></div>
                                        <h3 className="manage-collection-item-modal-title">
                                            Choose a collection to copy/move this item to.
                                        </h3>
                                    </div>
                                </div>
                            )
                        }

                        <div className="add-to-collection-body">
                            <div className="d-flex align-items-center justify-content-between p-4 pb-0">
                                <h6 className='collection-title m-0'>Collections</h6>
                                <button onClick={() => setNewCollection(true)} className="unset-all text-primary new-collection-btn">New Collection</button>
                            </div>
                            <div className="add-to-collection-collections overflow-scroll p-4 pt-0 mt-3">
                                {
                                    (loading) ? (<>
                                        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} borderRadius={10} height={64} width={'100%'} />)}
                                    </>) : (
                                        <>
                                            {
                                                sortedCollections.map((collection) => (
                                                    <div key={collection.uuid} className="collection-item">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="collection-thumbnail bgc-content bg-image" style={{ backgroundImage: `url(${collection.thumbnail_urls?.[collection.thumbnail_urls?.length - 1]})` }}></div>
                                                            <div className="d-flex flex-column gap-1">
                                                                <h6 className='collection-title m-0'>{collection.name}</h6>
                                                                <span className='collection-items-count'>{collection.count} {textToTitleCase((itemType === 'account' ? 'creator' : 'post') + (collection.count === 1 ? '' : 's'))}</span>
                                                            </div>
                                                        </div>
                                                        {
                                                            (processing === collection.uuid) ? (
                                                                <span className="spinner-border flex-shrink-0 text-primary" style={{ height: '1rem', width: '1rem' }} role="status">
                                                                    <span className="visually-hidden">Loading...</span>
                                                                </span>
                                                            ) : bookmarkCollections.includes(collection.uuid) ? (
                                                                <button className='unset-all' onClick={() => removeFromCollection(collection.uuid)}>
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                        <path fillRule="evenodd" clipRule="evenodd" d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12ZM16.0303 8.96967C16.3232 9.26256 16.3232 9.73744 16.0303 10.0303L11.0303 15.0303C10.7374 15.3232 10.2626 15.3232 9.96967 15.0303L7.96967 13.0303C7.67678 12.7374 7.67678 12.2626 7.96967 11.9697C8.26256 11.6768 8.73744 11.6768 9.03033 11.9697L10.5 13.4393L12.7348 11.2045L14.9697 8.96967C15.2626 8.67678 15.7374 8.67678 16.0303 8.96967Z" fill="#0BAC68" />
                                                                    </svg>
                                                                </button>
                                                            ) : (
                                                                <button className='unset-all' onClick={() => bookmark(collection.uuid)}>
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                        <path d="M12.75 9C12.75 8.58579 12.4142 8.25 12 8.25C11.5858 8.25 11.25 8.58579 11.25 9L11.25 11.25H9C8.58579 11.25 8.25 11.5858 8.25 12C8.25 12.4142 8.58579 12.75 9 12.75H11.25V15C11.25 15.4142 11.5858 15.75 12 15.75C12.4142 15.75 12.75 15.4142 12.75 15L12.75 12.75H15C15.4142 12.75 15.75 12.4142 15.75 12C15.75 11.5858 15.4142 11.25 15 11.25H12.75V9Z" fill="#111111" />
                                                                        <path fillRule="evenodd" clipRule="evenodd" d="M12 1.25C6.06294 1.25 1.25 6.06294 1.25 12C1.25 17.9371 6.06294 22.75 12 22.75C17.9371 22.75 22.75 17.9371 22.75 12C22.75 6.06294 17.9371 1.25 12 1.25ZM2.75 12C2.75 6.89137 6.89137 2.75 12 2.75C17.1086 2.75 21.25 6.89137 21.25 12C21.25 17.1086 17.1086 21.25 12 21.25C6.89137 21.25 2.75 17.1086 2.75 12Z" fill="#111111" />
                                                                    </svg>
                                                                </button>
                                                            )
                                                        }

                                                    </div>
                                                ))
                                            }
                                        </>
                                    )
                                }
                                {
                                    !loading && collections.length === 0 && (
                                        <div className="d-flex w-100 justify-content-center align-items-center">
                                            <div className="length-zero my-4">
                                                <h3>No collection yet!</h3>
                                            </div>
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                    </ModalBody>
                )
            }


        </Modal>
    )
}

export default ManageCollectionModal